/**
 * Monkey Worlds API helpers. Finite four-world campaign flow: start today's session, then report
 * each cleared level. The server owns the economy — coins depend only on the (world, slot) pair +
 * per-day paid set + cap, never on a client-supplied amount. stars/timeTicks are advisory
 * (analytics) only.
 */

import type { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { MONKEY_WORLDS_GAME_KEY } from '../constants/monkeyWorlds.constants';

type ApiClient = ReturnType<typeof useApiClient>;

export interface MonkeyWorldsStart {
  levelsPerWorld: number;
  worldCount: number;
  /** Packed per-world furthest-slot progress (byte per world, world 0 = LSB). */
  worldProgress: number;
  coinsLeftToday: number;
  capReached: boolean;
  /** "w:s" entries already paid today. */
  paidLevels: string[];
}

export interface MonkeyWorldsClearResult {
  coinsAwarded: number;
  /** New server coin balance; omitted for anonymous users (never banked). */
  coins?: number;
  coinsLeftToday: number;
  capReached: boolean;
  worldProgress: number;
  newBest: boolean;
}

export async function startMonkeyWorldsRun(apiClient: ApiClient): Promise<MonkeyWorldsStart> {
  const response = await apiClient<{ success: boolean; data: MonkeyWorldsStart; message?: string }>(
    API_ENDPOINTS.games.worldStart(MONKEY_WORLDS_GAME_KEY),
    { method: 'POST', data: {} },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not start the game');
  return payload.data;
}

export async function recordMonkeyWorldsLevelClear(
  apiClient: ApiClient,
  args: { world: number; slot: number; stars: 1 | 2 | 3; timeTicks: number },
): Promise<MonkeyWorldsClearResult> {
  const response = await apiClient<{ success: boolean; data: MonkeyWorldsClearResult; message?: string }>(
    API_ENDPOINTS.games.worldClear(MONKEY_WORLDS_GAME_KEY),
    { method: 'POST', data: args },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not record the level');
  return payload.data;
}
